/**
 * Class to render a timeline widget for Security log
 */
class SpbcTimelineWidget {
    // Static constants
    static markerRadius = 4;
    static hoursRangeMax = 24;
    static axisStepsCount = 6;
    static userActionsUpstep = 24;
    static legendCircleX = -120;
    static legendTextX = -105;
    static userLegendMaxRows = 2;
    static userLegendRowHeight = 18;
    static userLegendItemGap = 15;
    static userLegendAxisOffset = 60; // Offset from axis to user legend

    /**
     * Constructor.
     */
    constructor() {
        // Current time properties
        this.now = new Date();
        this.currentTimestamp = Math.floor(this.now.getTime() / 1000);

        // Data properties
        this.eventsData = null;
        this.timelineMarkersData = [];
        this.timelineKnownEventsData = [];
        this.timelineEventTooltip = null;
        this.timelineStartTimestamp = this.currentTimestamp - SpbcTimelineWidget.hoursRangeMax * 3600;
        this.usersCount = 0;
        this.summaryEventsCount = 0;
        this.userLegendExpanded = false;
        this.selectedUserValue = 'most_active__action';
    }

    /**
     * Initialize and render the timeline widget
     */
    render() {
        if (typeof spbcTimelineWidgetData === 'undefined') {
            return;
        }

        this.timelineEventTooltip = document.getElementById('spbc-timeline-tooltip');
        this.eventsData = spbcTimelineWidgetData.users_actions;
        if (spbcTimelineWidgetData.current_timestamp_local) {
            this.currentTimestamp = Number(spbcTimelineWidgetData.current_timestamp_local);
        }
        this.timelineStartTimestamp = Number(spbcTimelineWidgetData.first_event_timestamp);
        this.timelineKnownEventsData = spbcTimelineWidgetData.known_events_data;
        this.timelineMarkersData = this.generateMarkersData(spbcTimelineWidgetData.found_types);

        // Initialize timeline when tab is loaded
        window.addEventListener('spbctTabLoaded', (event) => {
            if (event.detail.tab === 'security_log') {
                this.initTimeline();
            }
        });

        jQuery('#spbc-timeline-tab').on('click', (e)=> {
            if (e.hasOwnProperty('originalEvent')) {
                if (localStorage.getItem('spbc_timeline_closed') !== '1') {
                    localStorage.setItem('spbc_timeline_closed', '1');
                } else {
                    localStorage.setItem('spbc_timeline_closed', '0');
                }
            }
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            if (!this.selectedUserValue) {
                return;
            }
            this.userLegendExpanded = false;
            if (this.selectedUserValue === 'most_active__action') {
                this.drawUserTimelineMostActive();
            } else {
                this.drawUserTimeline(this.selectedUserValue);
            }
        });
    }

    /**
     * Initialize timeline with user selection
     */
    initTimeline() {
        const wrapper = document.getElementById('spbc-timeline-user-select-wrapper');
        const trigger = document.getElementById('spbc-timeline-user-select-trigger');
        const dropdown = document.getElementById('spbc-timeline-user-select-dropdown');
        const valueDisplay = document.getElementById('spbc-timeline-user-select-value');
        const usersWithRecentEvents = [];

        // Calculate summary statistics for the last 24 hours only
        this.usersCount = 0;
        this.summaryEventsCount = 0;

        for (let key in this.eventsData) {
            if (this.eventsData.hasOwnProperty(key)) {
                const userData = this.eventsData[key];
                const eventsArray = Array.isArray(userData) ? userData : (userData.events || []);
                const recentEvents = eventsArray.filter((event) =>
                    event.time >= this.timelineStartTimestamp &&
                    event.time <= this.currentTimestamp,
                );

                if (recentEvents.length > 0) {
                    this.usersCount++;
                    this.summaryEventsCount += recentEvents.length;
                    usersWithRecentEvents.push({
                        user: key,
                        role: userData.role || 'unknown',
                        eventsCount: recentEvents.length,
                    });
                }
            }
        }

        // Reset dropdown options
        dropdown.innerHTML = '';

        // Helper to create option element
        const createOption = (text, value) => {
            const option = document.createElement('div');
            option.className = 'spbc-timeline-select-option';
            option.dataset.value = value;
            option.textContent = text;
            return option;
        };

        // Add "Most Active Users" option
        const mostActiveOption = createOption('Most Active Users', 'most_active__action');
        mostActiveOption.classList.add('selected');
        dropdown.appendChild(mostActiveOption);

        // Populate user options with role and event count
        usersWithRecentEvents.forEach((userData) => {
            const displayText = `${userData.user} (${userData.role}) (${userData.eventsCount} events)`;
            dropdown.appendChild(createOption(displayText, userData.user));
        });

        // Toggle dropdown on trigger click
        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            wrapper.classList.toggle('open');
        });

        // Close dropdown on outside click
        document.addEventListener('click', (e) => {
            if (!wrapper.contains(e.target)) {
                wrapper.classList.remove('open');
            }
        });

        // Handle option selection
        dropdown.addEventListener('click', (e) => {
            const option = e.target.closest('.spbc-timeline-select-option');
            if (!option) return;

            const selectedValue = option.dataset.value;
            this.selectedUserValue = selectedValue;
            this.userLegendExpanded = false;

            // Update display and selection state
            valueDisplay.textContent = option.textContent;
            dropdown.querySelectorAll('.spbc-timeline-select-option').forEach((opt) => {
                opt.classList.remove('selected');
            });
            option.classList.add('selected');

            // Close dropdown
            wrapper.classList.remove('open');

            if (selectedValue === 'most_active__action') {
                this.drawUserTimelineMostActive();
                const url = new URL(window.location);
                url.searchParams.delete('users');
                window.history.pushState({}, '', url);
            } else {
                this.drawUserTimeline(selectedValue);
                const url = new URL(window.location);
                url.searchParams.set('users', selectedValue);
                window.history.pushState({}, '', url);
            }

            // Update security log table with selected user
            updateSecurityLogTable();
        });

        // Draw initial timeline or show message
        if (usersWithRecentEvents.length > 0) {
            this.drawUserTimelineMostActive();
            this.selectedUserValue = 'most_active__action';
            valueDisplay.textContent = 'Most Active Users';
        } else {
            document.querySelector('.spbc-timeline-container').style.display = 'none';
            document.querySelector('.spbc-timeline-controls').style.display = 'none';
            document.getElementById('spbc-timeline-no-data').style.display = 'block';
        }

        const activitySelector = jQuery('#spbc_timeline_activity_counters')[0];
        activitySelector.innerText = activitySelector.innerText
            .replace('%users_count%', this.usersCount.toString())
            .replace('%events_count%', this.summaryEventsCount.toString());

        const isWidgetClosedByUser = (
            localStorage.getItem('spbc_timeline_closed') === '1'
        );
        if (!isWidgetClosedByUser) {
            setTimeout(function() {
                jQuery('#spbc-timeline-tab').trigger('click');
            }, 200);
        }
    }

    /**
     * Draw time axis on the timeline
     * @param {SVGElement} container - SVG container element
     * @param {number} width - Drawing area width
     * @param {number} height - Drawing area height
     * @param {boolean} roundToHour - Whether to round ticks to full hours
     */
    drawTimeAxis(container, width, height, roundToHour = false) {
        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const axis = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        height = height + 10;
        // Create main axis line
        axis.setAttribute('class', 'spbc-timeline-axis');
        axis.setAttribute('x1', '0');
        axis.setAttribute('y1', height);
        axis.setAttribute('x2', width);
        axis.setAttribute('y2', height);
        container.appendChild(axis);

        // Create axis ticks and labels
        if (roundToHour) {
            const firstHourTs = Math.ceil(this.timelineStartTimestamp / 3600) * 3600;
            for (let ts = firstHourTs; ts <= this.currentTimestamp; ts += 3600) {
                const x = ((ts - this.timelineStartTimestamp) / timeRange) * width;

                // Create tick mark
                const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                tick.setAttribute('class', 'spbc-timeline-axis');
                tick.setAttribute('x1', x);
                tick.setAttribute('y1', height);
                tick.setAttribute('x2', x);
                tick.setAttribute('y2', height + 5);
                container.appendChild(tick);

                const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                label.setAttribute('class', 'spbc-timeline-axis-text');
                label.setAttribute('x', x);
                label.setAttribute('y', height + 20);
                const h = new Date(ts * 1000).getUTCHours();
                label.textContent = `${h.toString().padStart(2, '0')}:00`;
                container.appendChild(label);
            }
        } else {
            for (let i = 0; i <= SpbcTimelineWidget.axisStepsCount; i++) {
                const timestamp = this.timelineStartTimestamp + (i / SpbcTimelineWidget.axisStepsCount) * timeRange;
                const x = (i / SpbcTimelineWidget.axisStepsCount) * width;

                // Create tick mark
                const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                tick.setAttribute('class', 'spbc-timeline-axis');
                tick.setAttribute('x1', x);
                tick.setAttribute('y1', height);
                tick.setAttribute('x2', x);
                tick.setAttribute('y2', height + 5);
                container.appendChild(tick);

                // Create time label
                const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                label.setAttribute('class', 'spbc-timeline-axis-text');
                label.setAttribute('x', x);
                label.setAttribute('y', height + 20);
                const date = new Date(timestamp * 1000);
                label.textContent =
                    `
                        ${date.getUTCHours().toString().padStart(2, '0')}:
                        ${date.getUTCMinutes().toString().padStart(2, '0')}
                    `;
                container.appendChild(label);
            }
        }
    }

    /**
     * Create SVG element for an event
     * @param {Object} event - Event data
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {string} user - User name
     * @param {number} radius - Marker radius
     * @param {boolean} isMostActive - Whether this is a synthetic marker for most active users view
     * @param {string} color - Optional color override for the marker
     * @return {SVGElement} Event group element
     */
    createEventElement(event, x, y, user, radius, isMostActive= false, color) {
        const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        group.setAttribute('transform', `translate(${x}, ${y})`);
        group.setAttribute('data-event', JSON.stringify({...event, user, isMostActive}));

        // Create event marker
        const marker = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        marker.setAttribute('class', 'spbc-timeline-event-marker');
        marker.setAttribute('r', radius || SpbcTimelineWidget.markerRadius);
        marker.style.fill = color || this.timelineMarkersData[event.event].color;
        group.appendChild(marker);

        // Add event handlers
        group.addEventListener('mouseenter', this.showEventTooltip.bind(this));
        group.addEventListener('mouseleave', this.hideEventTooltip.bind(this));

        if (isMostActive) {
            group.addEventListener('click', this.handleEventClick.bind(this, user));
        }

        return group;
    }

    /**
     * Show event tooltip
     * @param {Event} event - Mouse event
     */
    showEventTooltip(event) {
        const eventData = JSON.parse(event.currentTarget.getAttribute('data-event'));
        const {user, event: eventName, time, isMostActive} = eventData;

        // Format date for display
        const formatDate = (timestamp) => {
            const date = new Date(timestamp * 1000);

            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            const month = months[date.getUTCMonth()];
            const day = date.getUTCDate();
            const dayWithZero = String(day).padStart(2, '0');
            const year = date.getUTCFullYear();

            const hours = date.getUTCHours().toString().padStart(2, '0');
            const minutes = date.getUTCMinutes().toString().padStart(2, '0');
            const seconds = date.getUTCSeconds().toString().padStart(2, '0');

            return `${month} ${dayWithZero} ${year} ${hours}:${minutes}:${seconds}`;
        };

        // Update tooltip content
        document.getElementById('spbc-timeline-tooltip--user').innerText = user;
        document.getElementById('spbc-timeline-tooltip--event').innerText =
            eventData.count > 1 ? `${eventName} (${eventData.count})` : eventName;

        const timeRow = document.getElementById('spbc-timeline-tooltip--time').parentElement;
        if (isMostActive) {
            timeRow.style.display = 'none';
        } else {
            timeRow.style.display = '';
            document.getElementById('spbc-timeline-tooltip--time').innerText = formatDate(time);
        }

        const tooltip= document.getElementById('spbc-timeline-tooltip');
        const fixedTop = event.offsetY + 35;
        const tooltipY = fixedTop.toString() + 'px';
        const screenWidth = window.innerWidth;
        const tooltipWidth = tooltip.offsetWidth ? tooltip.offsetWidth : 165;
        const tooltipEventPositionX = event.clientX;
        let tooltipX = tooltipEventPositionX - tooltipWidth;
        if (tooltipEventPositionX + (tooltipWidth * 2) > screenWidth) {
            tooltipX = tooltipEventPositionX - (tooltipWidth * 2);
        }

        tooltipX = tooltipX.toString() + 'px';
        tooltip.style.position = 'absolute';
        tooltip.style.top = tooltipY;
        tooltip.style.left = tooltipX;

        // Show tooltip
        this.timelineEventTooltip.classList.remove('spbc-timeline-hidden-element');
        jQuery('[data-event-type]').filter(function() {
            return jQuery(this).data('event-type') === eventName;
        }).addClass('spbc-timelime-highlight-on-tooltip');
    }

    /**
     * Hide event tooltip
     */
    hideEventTooltip() {
        this.timelineEventTooltip.classList.add('spbc-timeline-hidden-element');
        jQuery('[data-event-type]').removeClass('spbc-timelime-highlight-on-tooltip');
    }

    /**
     * Handle click on event marker - filter security log table by user
     * @param {string} user - User name
     * @param {Event} event - Click event
     */
    handleEventClick(user) {
        this.hideEventTooltip();

        // Update custom dropdown selector to show selected user
        const dropdown = document.getElementById('spbc-timeline-user-select-dropdown');
        const valueDisplay = document.getElementById('spbc-timeline-user-select-value');
        if (dropdown && valueDisplay) {
            this.selectedUserValue = user;
            // Find the option with matching value
            const options = dropdown.querySelectorAll('.spbc-timeline-select-option');
            options.forEach((opt) => {
                opt.classList.remove('selected');
                if (opt.dataset.value === user) {
                    opt.classList.add('selected');
                    valueDisplay.textContent = opt.textContent;
                }
            });
        }

        // Redraw timeline for the selected user
        this.drawUserTimeline(user);

        // Update URL with users parameter
        const url = new URL(window.location);
        url.searchParams.set('users', user);
        window.history.pushState({}, '', url);

        // Update security log table with selected user
        updateSecurityLogTable();
    }

    /**
     * Get most active users from last 24 hours (max 15 users)
     * @param {Object} eventsData - User events data
     * @return {Array} Array of user objects with their events
     */
    getMostActiveUsers(eventsData) {
        const currentTime = this.currentTimestamp;
        const twentyFourHoursAgo = currentTime - (24 * 3600);

        // Filter users with events in last 24 hours and count their events
        const usersWithRecentEvents = [];

        for (let user in eventsData) {
            if (eventsData.hasOwnProperty(user)) {
                const userData = eventsData[user];
                const eventsArray = Array.isArray(userData) ? userData : (userData.events || []);

                // Filter events from last 24 hours
                const recentEvents = eventsArray.filter((event) => event.time >= twentyFourHoursAgo);

                if (recentEvents.length > 0) {
                    usersWithRecentEvents.push({
                        user: user,
                        events: recentEvents,
                        count: recentEvents.length,
                        role: userData.role || 'unknown',
                    });
                }
            }
        }

        // Sort by event count (descending) and take top 15
        usersWithRecentEvents.sort((a, b) => b.count - a.count);

        return usersWithRecentEvents.slice(0, 15);
    }

    /**
     * Group events into a 24-element array (one slot per calendar hour).
     * @param {string} user
     * @param {Array}  events  - array of {event, time}
     * @return {Array} 24 items: {hour, user, count, byType}
     */
    groupByHour(user, events) {
        // Group events by actual hour slots (using real timestamps, not just hour of day)
        const slots = {};

        events.forEach((ev) => {
            // Round down to the start of the hour
            const hourStart = Math.floor(ev.time / 3600) * 3600;

            if (!slots[hourStart]) {
                slots[hourStart] = {
                    timestamp: hourStart + 1800, // midpoint of the hour
                    user: user,
                    count: 0,
                    byType: {},
                };
            }

            slots[hourStart].count++;
            slots[hourStart].byType[ev.event] = (slots[hourStart].byType[ev.event] || 0) + 1;
        });

        return Object.values(slots);
    }

    /**
     * Draw timeline for specific user
     * @param {string} user - Selected user name
     */
    drawUserTimeline(user) {
        const svg = document.getElementById('spbc-timeline-svg');
        const noDataMessage = document.getElementById('spbc-timeline-no-data');

        // Clear previous content
        svg.innerHTML = '';
        noDataMessage.style.display = 'none';

        let additionalEventsHeight = 0;
        if (spbcTimelineWidgetData.found_types.length > 5) {
            additionalEventsHeight = (spbcTimelineWidgetData.found_types.length - 5) * 12;
        }

        // Calculate dimensions
        const width = svg.parentElement.clientWidth;
        const height = 150 + additionalEventsHeight + SpbcTimelineWidget.userLegendAxisOffset;

        // Setup drawing area
        const margin = {top: 10 + additionalEventsHeight, right: 40, bottom: 40, left: 140};
        const contentWidth = width - margin.left - margin.right;
        const contentHeight = height - margin.top - margin.bottom;
        const baseLineY = contentHeight / 2;

        svg.setAttribute('width', width);
        svg.setAttribute('height', height);

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `translate(${margin.left}, ${margin.top+30})`);
        svg.appendChild(g);

        // Calculate base positions


        // Draw axis
        this.drawTimeAxis(g, contentWidth, baseLineY + 10);

        // Filter user events
        const userData = this.eventsData[user];
        const userEvents = (Array.isArray(userData) ? userData : (userData.events || [])).filter((event) =>
            event.time >= this.timelineStartTimestamp &&
            event.time <= this.currentTimestamp,
        );

        // Handle no events case
        if (userEvents.length === 0) {
            noDataMessage.style.display = 'block';
            return;
        }

        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const uniqueEventsCount = spbcTimelineWidgetData.found_types.length;

        // Draw event lines and legends
        for (let i = 0; i < uniqueEventsCount; i++) {
            const userLineY = baseLineY - (SpbcTimelineWidget.userActionsUpstep * i);
            const eventType = spbcTimelineWidgetData.found_types[i];
            const color = this.timelineMarkersData[eventType].color;

            // Draw event line
            const userLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            userLine.setAttribute('class', 'spbc-timeline-user-line');
            userLine.setAttribute('x1', '0');
            userLine.setAttribute('y1', userLineY);
            userLine.setAttribute('x2', contentWidth);
            userLine.setAttribute('y2', userLineY);
            userLine.setAttribute('data-event-type', eventType);
            g.appendChild(userLine);

            // Draw legend circle
            const legendCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            legendCircle.setAttribute('class', 'spbc-timeline-legend-circle');
            legendCircle.setAttribute('cx', SpbcTimelineWidget.legendCircleX);
            legendCircle.setAttribute('cy', userLineY);
            legendCircle.setAttribute('r', SpbcTimelineWidget.markerRadius);
            legendCircle.style.fill = color;
            g.appendChild(legendCircle);

            // Draw legend text
            const legendText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            legendText.setAttribute('class', 'spbc-timeline-legend-text');
            legendText.setAttribute('x', SpbcTimelineWidget.legendTextX);
            legendText.setAttribute('y', userLineY);
            legendText.setAttribute('text-anchor', 'start');
            legendText.setAttribute('dominant-baseline', 'middle');
            legendText.setAttribute('data-event-type', eventType);
            legendText.textContent = eventType;
            g.appendChild(legendText);
        }

        // Draw events
        userEvents.forEach((userEvent) => {
            const x = ((userEvent.time - this.timelineStartTimestamp) / timeRange) * contentWidth;
            const eventY = baseLineY + this.timelineMarkersData[userEvent.event].upstep;
            g.appendChild(this.createEventElement(userEvent, x, eventY, user));
        });
    }

    /**
     * Measure text width in SVG
     * @param {SVGElement} svg - SVG container element
     * @param {string} text - Text to measure
     * @param {string} className - CSS class for text styling
     * @return {number} Text width in pixels
     */
    measureTextWidth(svg, text, className = 'spbc-timeline-legend-text') {
        const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        tempText.setAttribute('class', className);
        tempText.textContent = text;
        tempText.style.visibility = 'hidden';
        svg.appendChild(tempText);
        const width = tempText.getComputedTextLength();
        svg.removeChild(tempText);
        return width;
    }

    /**
     * Calculate how many rows the user legend will occupy
     * @param {SVGElement} svg - SVG container element
     * @param {Array} users - Array of user data objects
     * @param {number} maxWidth - Maximum width for the legend area
     * @param {number|null} maxRows - Maximum rows limit (null for unlimited)
     * @return {number} Number of rows needed
     */
    calculateUserLegendRowsCount(svg, users, maxWidth, maxRows = null) {
        const itemGap = SpbcTimelineWidget.userLegendItemGap;
        const circleRadius = SpbcTimelineWidget.markerRadius;
        const circleTextGap = 6;
        const startX = 0;

        // Calculate "and N more" text width for reservation
        const moreTextTemplate = 'and 99 more';
        const moreTextWidth = this.measureTextWidth(svg, moreTextTemplate) + itemGap;

        let currentX = startX;
        let currentRow = 0;

        for (let i = 0; i < users.length; i++) {
            const userData = users[i];
            const textWidth = this.measureTextWidth(svg, userData.user);
            const itemWidth = circleRadius * 2 + circleTextGap + textWidth + itemGap;

            // Check if we need to reserve space for "and N more" on last allowed row
            const isLastRow = maxRows !== null && currentRow === maxRows - 1;
            const reserveForMore = isLastRow && (i < users.length - 1);
            const effectiveMaxWidth = reserveForMore ? maxWidth - moreTextWidth : maxWidth;

            if (currentX + itemWidth > effectiveMaxWidth && currentX > startX) {
                currentRow++;
                currentX = startX;

                // If we hit maxRows limit, stop counting
                if (maxRows !== null && currentRow >= maxRows) {
                    return maxRows;
                }
            }

            currentX += itemWidth;
        }

        return currentRow + 1; // +1 because rows are 0-indexed
    }

    /**
     * Draw timeline for most active users
     */
    drawUserTimelineMostActive() {
        const svg = document.getElementById('spbc-timeline-svg');
        const noDataMessage = document.getElementById('spbc-timeline-no-data');

        // Clear previous content
        svg.innerHTML = '';
        noDataMessage.style.display = 'none';

        // Get most active users
        const mostActiveUsers = this.getMostActiveUsers(this.eventsData);

        if (mostActiveUsers.length === 0) {
            noDataMessage.style.display = 'block';
            return;
        }

        let additionalEventsHeight = 0;
        if (spbcTimelineWidgetData.found_types.length > 5) {
            additionalEventsHeight = (spbcTimelineWidgetData.found_types.length - 5) * 12;
        }

        // Pre-assign a color to each user
        const randomColors = [
            '#0284A8', '#E74C3C', '#2ECC71', '#F39C12', '#9B59B6',
            '#1ABC9C', '#E67E22', '#3498DB', '#E91E63', '#00BCD4',
            '#8BC34A', '#FF5722', '#607D8B', '#795548', '#673AB7',
        ];
        const userColors = {};
        mostActiveUsers.forEach((userData, i) => {
            userColors[userData.user] = randomColors[(i + 1) % randomColors.length];
        });

        // Calculate dimensions - need to know content width first for row calculation
        const width = svg.parentElement.clientWidth;
        const margin = {top: 10 + additionalEventsHeight, right: 40, bottom: 40, left: 140};
        const contentWidth = width - margin.left - margin.right;

        // Calculate user legend rows count based on expanded state
        const userLegendRowHeight = SpbcTimelineWidget.userLegendRowHeight;
        const maxRows = this.userLegendExpanded ? null : SpbcTimelineWidget.userLegendMaxRows;
        const actualRowsCount = this.calculateUserLegendRowsCount(svg, mostActiveUsers, contentWidth, maxRows);
        const userLegendHeight = actualRowsCount * userLegendRowHeight;

        // Calculate height: base height (like drawUserTimeline) + user legend space
        const baseHeight = 150 + additionalEventsHeight + SpbcTimelineWidget.userLegendAxisOffset;
        const height = baseHeight + userLegendHeight;

        // Setup drawing area - baseLineY calculated from baseHeight (same as drawUserTimeline)
        const baseContentHeight = baseHeight - margin.top - margin.bottom;
        const baseLineY = baseContentHeight / 2;

        svg.setAttribute('width', width);
        svg.setAttribute('height', height);

        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('transform', `translate(${margin.left}, ${margin.top+30})`);
        svg.appendChild(g);

        // Draw axis
        this.drawTimeAxis(g, contentWidth, baseLineY + 10, true);

        const timeRange = this.currentTimestamp - this.timelineStartTimestamp;
        const uniqueEventsCount = spbcTimelineWidgetData.found_types.length;

        // Draw event lines and legends
        for (let i = 0; i < uniqueEventsCount; i++) {
            const userLineY = baseLineY - (SpbcTimelineWidget.userActionsUpstep * i);
            const eventType = spbcTimelineWidgetData.found_types[i];
            const color = this.timelineMarkersData[eventType].color;

            // Draw event line
            const userLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            userLine.setAttribute('class', 'spbc-timeline-user-line');
            userLine.setAttribute('x1', '0');
            userLine.setAttribute('y1', userLineY);
            userLine.setAttribute('x2', contentWidth);
            userLine.setAttribute('y2', userLineY);
            userLine.setAttribute('data-event-type', eventType);
            g.appendChild(userLine);

            // Draw legend circle
            const legendCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            legendCircle.setAttribute('class', 'spbc-timeline-legend-circle');
            legendCircle.setAttribute('cx', SpbcTimelineWidget.legendCircleX);
            legendCircle.setAttribute('cy', userLineY);
            legendCircle.setAttribute('r', SpbcTimelineWidget.markerRadius);
            legendCircle.style.fill = color;
            g.appendChild(legendCircle);

            // Draw legend text
            const legendText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            legendText.setAttribute('class', 'spbc-timeline-legend-text');
            legendText.setAttribute('x', SpbcTimelineWidget.legendTextX);
            legendText.setAttribute('y', userLineY);
            legendText.setAttribute('text-anchor', 'start');
            legendText.setAttribute('dominant-baseline', 'middle');
            legendText.setAttribute('data-event-type', eventType);
            legendText.textContent = eventType;
            g.appendChild(legendText);
        }

        // Draw user color legend (below axis)
        const userLegendStartY = baseLineY + SpbcTimelineWidget.userLegendAxisOffset;
        this.drawUserLegend(svg, g, mostActiveUsers, userColors, userLegendStartY, contentWidth);

        // Draw events for all most active users, grouped by hour
        mostActiveUsers.forEach((userData) => {
            const eventColor = userColors[userData.user];

            const hourly = this.groupByHour(userData.user, userData.events);
            hourly.forEach((slot) => {
                if (slot.count === 0) return;
                const x = Math.max(
                    0,
                    Math.min(
                        ((slot.timestamp - this.timelineStartTimestamp) / timeRange) * contentWidth, contentWidth,
                    ),
                );
                Object.entries(slot.byType).forEach(([eventType, cnt]) => {
                    const eventY = baseLineY + this.timelineMarkersData[eventType].upstep;
                    const radius = SpbcTimelineWidget.markerRadius + Math.min(Math.sqrt(cnt - 1) * 2, 8);
                    const syntheticEvent = {event: eventType, time: slot.timestamp, count: cnt};
                    g.appendChild(
                        this.createEventElement(
                            syntheticEvent,
                            x,
                            eventY,
                            userData.user,
                            radius,
                            true,
                            eventColor,
                        ),
                    );
                });
            });
        });
    }

    /**
     * Draw user legend with horizontal layout and row limit
     * @param {SVGElement} svg - SVG element
     * @param {SVGElement} g - Group element to append to
     * @param {Array} users - Array of user data objects
     * @param {Object} userColors - Map of user to color
     * @param {number} startY - Starting Y position
     * @param {number} maxWidth - Maximum width for the legend area
     */
    drawUserLegend(svg, g, users, userColors, startY, maxWidth) {
        const rowHeight = SpbcTimelineWidget.userLegendRowHeight;
        const itemGap = SpbcTimelineWidget.userLegendItemGap;
        const circleRadius = SpbcTimelineWidget.markerRadius;
        const circleTextGap = 6;
        const startX = 0;

        // Determine max rows: null for expanded (unlimited), or limit for collapsed
        const maxRows = this.userLegendExpanded ? null : SpbcTimelineWidget.userLegendMaxRows;

        // Horizontal layout with optional row limit
        let currentX = startX;
        let currentRow = 0;
        let visibleCount = 0;
        let lastRowEndX = startX; // Track end position on the last allowed row for "and N more" button

        // Calculate "and N more" text width for reservation
        const moreTextTemplate = 'and 99 more';
        const moreTextWidth = this.measureTextWidth(svg, moreTextTemplate) + itemGap;

        for (let i = 0; i < users.length; i++) {
            const userData = users[i];
            const textWidth = this.measureTextWidth(svg, userData.user);
            const itemWidth = circleRadius * 2 + circleTextGap + textWidth + itemGap;

            // Check if we need to wrap to next row
            const isLastRow = maxRows !== null && currentRow === maxRows - 1;
            const reserveForMore = isLastRow && (i < users.length - 1);
            const effectiveMaxWidth = reserveForMore ? maxWidth - moreTextWidth : maxWidth;

            if (currentX + itemWidth > effectiveMaxWidth && currentX > startX) {
                // Before moving to next row, save position if we're on the last allowed row
                if (isLastRow) {
                    lastRowEndX = currentX;
                }

                currentRow++;
                currentX = startX;

                // Check if we exceeded max rows (only when maxRows is set)
                if (maxRows !== null && currentRow >= maxRows) {
                    // Draw "and N more" link at the end of the last row (continuation of the line)
                    const remainingCount = users.length - visibleCount;
                    if (remainingCount > 0) {
                        const moreText = `and ${remainingCount} more`;
                        const ly = startY + (maxRows - 1) * rowHeight;

                        const moreLink = document.createElementNS('http://www.w3.org/2000/svg', 'text');
                        moreLink.setAttribute('class', 'spbc-timeline-legend-text spbc-timeline-more-link');
                        moreLink.setAttribute('x', lastRowEndX);
                        moreLink.setAttribute('y', ly);
                        moreLink.setAttribute('text-anchor', 'start');
                        moreLink.setAttribute('dominant-baseline', 'middle');
                        moreLink.textContent = moreText;
                        moreLink.style.cursor = 'pointer';
                        moreLink.style.fill = '#0073aa';
                        moreLink.style.textDecoration = 'underline';
                        moreLink.addEventListener('click', () => {
                            this.userLegendExpanded = true;
                            this.drawUserTimelineMostActive();
                        });
                        g.appendChild(moreLink);
                    }
                    return;
                }
            }

            // Track position on the last allowed row
            if (maxRows !== null && currentRow === maxRows - 1) {
                lastRowEndX = currentX + itemWidth;
            }

            // Draw user legend item
            const ly = startY + currentRow * rowHeight;
            const cx = currentX + circleRadius;
            const tx = currentX + circleRadius * 2 + circleTextGap;

            const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            circle.setAttribute('cx', cx);
            circle.setAttribute('cy', ly);
            circle.setAttribute('r', circleRadius);
            circle.style.fill = userColors[userData.user];
            g.appendChild(circle);

            const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            text.setAttribute('class', 'spbc-timeline-legend-text');
            text.setAttribute('x', tx);
            text.setAttribute('y', ly);
            text.setAttribute('text-anchor', 'start');
            text.setAttribute('dominant-baseline', 'middle');
            text.textContent = userData.user;
            g.appendChild(text);

            currentX += itemWidth;
            visibleCount++;
        }
    }

    /*
    /!**
     * Updates the security log table based on selected user
     * @param {string} selectedUser - The selected user or 'most_active__action'
     *!/
    updateSecurityLogTable(selectedUser) {
        const data= {};
        const urlParams = new URLSearchParams(window.location.search);
        const usersParam = urlParams.get('users');
        if (usersParam) {
            data.users = usersParam;
        }
        spbcSendAJAXRequest(data, params, obj);
    }*/

    /**
     * Generate marker data for events
     * @param {Array} events - List of event types
     * @return {Object} Marker configuration data
     */
    generateMarkersData(events) {
        const colorMap = {};
        let upstep = 0;
        let colorScheme = 'default';

        events.forEach((userEvent) => {
            if (this.timelineKnownEventsData[userEvent] && this.timelineKnownEventsData[userEvent].color_scheme) {
                colorScheme = this.timelineKnownEventsData[userEvent].color_scheme;
            }
            colorMap[userEvent] = {
                color: colorScheme,
                upstep: upstep,
            };
            upstep -= SpbcTimelineWidget.userActionsUpstep;
        });

        return colorMap;
    }
}

/**
 * Wrapper
 */
function spbcTimelineWidgetRender() { // eslint-disable-line no-unused-vars
    const timelineWidget = new SpbcTimelineWidget();
    jQuery( function() {
        jQuery('#spbc-timeline-accordion').accordion({
            heightStyle: 'content',
            collapsible: true,
            active: false,
        });
    } );
    timelineWidget.render();
}


